import { useEffect, useState } from "react";
import { Link, useInRouterContext } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getApiErrorMessage } from "@/lib/api";
import { formatCnpj } from "@/lib/format";
import {
  createEstablishment,
  deactivateEstablishment,
  listEstablishments,
  type Establishment,
} from "@/lib/adminEstablishmentsApi";

/**
 * Cadastro dos estabelecimentos PRÓPRIOS da Naturexpress — a empresa (CNPJ) que recebe as NFs de entrada e é
 * dona do certificado A1. É o que o seletor da tela de Certificado digital e os filtros de NF usam.
 */
export default function AdminEstablishments() {
  // O detalhe é uma rota própria; fora de um Router (ex.: teste unitário isolado) caímos para texto puro.
  const inRouter = useInRouterContext();
  const [list, setList] = useState<Establishment[]>([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [cnpj, setCnpj] = useState("");
  const [name, setName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      setList(await listEstablishments());
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao carregar estabelecimentos"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const create = async () => {
    const digits = cnpj.replace(/\D/g, "");
    if (digits.length !== 14) { toast.error("Informe um CNPJ com 14 dígitos."); return; }
    if (!name.trim()) { toast.error("Informe o nome/razão social."); return; }
    setBusy(true);
    try {
      await createEstablishment(digits, name.trim());
      toast.success("Estabelecimento cadastrado.");
      setCnpj(""); setName("");
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao cadastrar"));
    } finally {
      setBusy(false);
    }
  };

  const deactivate = async (est: Establishment) => {
    setBusy(true);
    try {
      await deactivateEstablishment(est.id);
      toast.success("Estabelecimento desativado.");
      await load();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "Falha ao desativar"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container max-w-4xl py-6 space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Estabelecimentos</h1>
        <p className="text-sm text-muted-foreground">
          As empresas (CNPJ) da Naturexpress que recebem notas fiscais de entrada e são donas do certificado
          digital. Cadastre aqui a sua empresa para usar nas Notas de entrada e no Certificado digital.
        </p>
      </header>

      <section className="rounded-xl border border-border p-4 space-y-3">
        <div className="text-sm font-semibold">Novo estabelecimento</div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            CNPJ
            <input
              type="text"
              value={cnpj}
              onChange={(e) => setCnpj(e.target.value)}
              placeholder="00.000.000/0000-00"
              className="border rounded px-2 py-1 text-sm text-foreground"
              aria-label="CNPJ do estabelecimento"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            Nome / razão social
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="NATUREXPRESS PRODUTOS ALIMENTICIOS LTDA"
              className="border rounded px-2 py-1 text-sm text-foreground min-w-80"
              aria-label="Nome do estabelecimento"
            />
          </label>
          <Button size="sm" onClick={() => void create()} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Cadastrar
          </Button>
        </div>
      </section>

      <section className="rounded-xl border border-border p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold">
          Cadastrados {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        {list.length === 0 ? (
          <div className="text-xs text-muted-foreground">Nenhum estabelecimento cadastrado.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="p-1">nome</th>
                  <th className="p-1">CNPJ</th>
                  <th className="p-1">situação</th>
                  <th className="p-1"></th>
                </tr>
              </thead>
              <tbody>
                {list.map((est) => (
                  <tr key={est.id} className="border-t border-border">
                    <td className="p-1">
                      {inRouter ? (
                        <Link
                          to={`/admin/estabelecimentos/${encodeURIComponent(est.id)}`}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {est.name}
                        </Link>
                      ) : (
                        est.name
                      )}
                    </td>
                    <td className="p-1">{formatCnpj(est.cnpj)}</td>
                    <td className="p-1">{est.active ? "ativo" : "inativo"}</td>
                    <td className="p-1">
                      {est.active && (
                        <Button size="sm" variant="ghost" disabled={busy} onClick={() => void deactivate(est)}>
                          <Ban className="h-3 w-3" /> Desativar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
